import { useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useOutlet, useNavigationType } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useViewTransition } from '~/hooks/useViewTransition';
import { useAppBackFlag } from '~/hooks/useAppBackFlag';

/**
 * アニメーション対象のルート判定
 */
function shouldAnimate(fromPath: string, toPath: string): boolean {
  // 顧客詳細ページへの遷移、または顧客詳細ページからの遷移のみアニメーション
  const isToCustomerDetail = toPath.startsWith('/customers/');
  const isFromCustomerDetail = fromPath.startsWith('/customers/');

  return isToCustomerDetail || isFromCustomerDetail;
}

/**
 * アニメーション付きOutletコンポーネント
 *
 * View Transitions APIを使用して特定の画面遷移にアニメーションを追加
 *
 * アニメーション戦略（PWA定石）:
 * - PUSH（進む）: 常にforwardアニメーション
 * - POP（戻る） + fromAppBack: backアニメーション（アプリ内戻るボタン）
 * - POP（戻る）のみ: アニメーションなし（端末スワイプバック）
 *
 * この戦略により、端末のスワイプバックジェスチャーとView Transitionsの
 * 二重アニメーションを防ぎます。
 */
export default function AnimatedOutlet() {
  const outlet = useOutlet();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { isSupported } = useViewTransition();
  const [displayedLocation, setDisplayedLocation] = useState(location);
  const [displayedOutlet, setDisplayedOutlet] = useState(outlet);
  const transitionRef = useRef<boolean>(false);

  // ロケーション変更時にView Transitionを実行
  useLayoutEffect(() => {
    // 同じロケーションの場合はスキップ
    if (displayedLocation.key === location.key) return;

    // 既にトランジション実行中の場合はスキップ（アニメーションなしで状態更新）
    if (transitionRef.current) {
      setDisplayedLocation(location);
      setDisplayedOutlet(outlet);
      return;
    }

    // View Transitions API未対応の場合は即座に更新
    if (!isSupported || typeof document.startViewTransition !== 'function') {
      setDisplayedLocation(location);
      setDisplayedOutlet(outlet);
      return;
    }

    // アニメーション対象外の遷移はスキップ
    if (!shouldAnimate(displayedLocation.pathname, location.pathname)) {
      setDisplayedLocation(location);
      setDisplayedOutlet(outlet);
      return;
    }

    // アニメーション判定: グローバルフラグをチェック
    const { isAppBack, reset } = useAppBackFlag();
    const fromAppBack = navigationType === 'POP' && isAppBack();
    const shouldAnimateTransition =
      navigationType === 'PUSH' || // 進むは常にアニメ
      fromAppBack; // 戻るはアプリ内ボタンのみ

    // 端末スワイプバック（POP && !fromAppBack）はアニメーションなし
    if (!shouldAnimateTransition) {
      reset(); // フラグをリセット
      setDisplayedLocation(location);
      setDisplayedOutlet(outlet);
      return;
    }

    // View Transitionを開始
    try {
      // トランジション実行中フラグを立てる
      transitionRef.current = true;

      const html = document.documentElement;

      // 方向を判定: POPは常にback、PUSHは常にforward
      const direction = navigationType === 'POP' ? 'back' : 'forward';

      // 方向を設定
      html.dataset.direction = direction;

      const transition = (document as any).startViewTransition(() => {
        // flushSyncで同期的にReactを更新
        flushSync(() => {
          setDisplayedLocation(location);
          setDisplayedOutlet(outlet);
        });
      });

      transition.finished
        .catch((error: Error) => {
          console.error('[ViewTransition] Error:', error);
        })
        .finally(() => {
          // data属性をクリーンアップ
          delete html.dataset.direction;
          // トランジション実行中フラグをクリア
          transitionRef.current = false;
          // アプリ内戻るフラグをリセット
          reset();
        });
    } catch (error) {
      console.error('[ViewTransition] Failed to start:', error);
      // エラー時もフラグをクリア
      transitionRef.current = false;
      reset();
      setDisplayedLocation(location);
      setDisplayedOutlet(outlet);
    }
  }, [displayedLocation, isSupported, location, outlet, navigationType]);

  return (
    <div
      className="h-full"
      data-view-transition={isSupported ? 'enabled' : 'disabled'}
      style={{ viewTransitionName: 'main-content' }}
    >
      {/* スナップショットされたロケーションのoutletをレンダリング */}
      {displayedOutlet}
    </div>
  );
}
